/* ============================================
BOOST CUSTOMISATION START
Collection card colour-thumbnail hover behaviour + PDP colour selector tooltip & badge.

----- Collection card behaviour -----
On collection pages, hovering a colour thumbnail under a product card:
  1. Swaps the main card image to that colour variant's image.
  2. Reveals a small colour-name badge near the top of the image.
  3. Shows a custom floating tooltip above the thumbnail (the colour name).
Hovering the ">" chevron:
  1. Leaves the main image in place.
  2. Shows a tooltip "Show All Colours".
On mouse-leave: image, badge, and tooltip all clear.

----- PDP colour selector behaviour -----
On product pages, the colour selector (.boost-pdp-colour-list):
  1. Shows the same floating tooltip above the hovered swatch with the colour name.
  2. Shows a persistent badge at the TOP-CENTRE of the main gallery image with
     the currently selected colour name.
  3. The currently selected colour drives variant resolution via the standard
     Shopify options[] mechanism (no event interception).

Implementation notes:
- Tooltip is a single floating element appended to <body> and positioned with
  getBoundingClientRect, which lets it escape any overflow:hidden or stacking
  context on the product card. Reads from data-tooltip="..." on the target.
- Uses event delegation so it works for cards added by any future
  ajax/load-more/infinite-scroll behaviour.
- The PDP main-image badge is positioned absolutely at the top-centre of the
  .l4pr gallery via CSS (see theme-xtra.css .boost-pdp-colour-badge).
- We do NOT use a 'change' event listener on the PDP colour radios. The
  theme's variant-selection JS (custom-async.js) uses capture-phase listeners
  and intercepting those caused the radios to revert. Instead we:
    - Use click on the label to update the main-image badge.
    - Use a MutationObserver on the .f8pr-variant-selection parent to detect
      when the theme replaces the variant selection HTML after a size change,
      and reattach the badge / refresh it from whichever radio is checked
      in the new markup.
============================================ */
(function () {
  'use strict';

  var THUMB_SELECTOR = '.boost-colour-thumbnail-link';
  var MORE_SELECTOR = '.boost-colour-more-link, .boost-colour-nav';
  var TIP_TARGETS = THUMB_SELECTOR + ', ' + MORE_SELECTOR;

  var FIGURE_SELECTOR = '.boost-card-figure';
  var BADGE_SELECTOR = '.boost-hover-badge';
  var IMG_SELECTOR = '.boost-card-main-picture img';

  // -------- Singleton floating tooltip --------------------------------------
  // Signature: showTip(targetEl, text) — positions tooltip above targetEl.
  // The PDP swatch handler previously called this with the wrong argument
  // shape — that bug is now fixed.

  var tipEl = null;

  function getTip() {
    if (tipEl) return tipEl;
    tipEl = document.createElement('div');
    tipEl.className = 'boost-tooltip';
    tipEl.setAttribute('role', 'tooltip');
    tipEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tipEl);
    return tipEl;
  }

  function showTip(targetEl, text) {
    if (!text || !targetEl || typeof targetEl.getBoundingClientRect !== 'function') return;
    var t = getTip();
    t.textContent = text;
    t.style.left = '-9999px';
    t.style.top = '-9999px';
    t.classList.add('boost-tooltip-visible');

    var rect = targetEl.getBoundingClientRect();
    var tipRect = t.getBoundingClientRect();
    var left = rect.left + (rect.width / 2) - (tipRect.width / 2);
    var top = rect.top - tipRect.height - 8;

    var minLeft = 4;
    var maxLeft = window.innerWidth - tipRect.width - 4;
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    if (top < 4) {
      top = rect.bottom + 8;
      t.classList.add('boost-tooltip-below');
    } else {
      t.classList.remove('boost-tooltip-below');
    }

    t.style.left = Math.round(left + window.scrollX) + 'px';
    t.style.top = Math.round(top + window.scrollY) + 'px';
  }

  function hideTip() {
    if (!tipEl) return;
    tipEl.classList.remove('boost-tooltip-visible');
    tipEl.classList.remove('boost-tooltip-below');
    tipEl.textContent = '';
  }

  // -------- Image-swap context helpers (collection cards) -------------------

  function getCardContext(thumbEl) {
    var card = thumbEl.closest('.product-card') || thumbEl.closest('.product-item');
    if (!card) return null;
    var figure = card.querySelector(FIGURE_SELECTOR);
    var badge = card.querySelector(BADGE_SELECTOR);
    var img = card.querySelector(IMG_SELECTOR);
    if (!figure || !badge || !img) return null;
    return { card: card, figure: figure, badge: badge, img: img };
  }

  // Fade duration must match the CSS transition duration applied to
  // .boost-card-main-picture img. Keep these in sync if you change one.
  var FADE_MS = 180;

  function fadeSwap(img, newSrc, srcsetValue) {
    var token = (img.__boostSwapToken || 0) + 1;
    img.__boostSwapToken = token;

    img.style.opacity = '0';

    var done = false;
    var commit = function () {
      if (done) return;
      done = true;
      img.removeEventListener('transitionend', onEnd);
      if (img.__boostSwapToken !== token) return;
      if (typeof newSrc === 'string') {
        if (newSrc) img.setAttribute('src', newSrc);
      }
      if (srcsetValue === null) {
        img.removeAttribute('srcset');
      } else if (typeof srcsetValue === 'string') {
        img.setAttribute('srcset', srcsetValue);
      }
      requestAnimationFrame(function () {
        if (img.__boostSwapToken !== token) return;
        img.style.opacity = '1';
      });
    };
    var onEnd = function (e) {
      if (e.propertyName !== 'opacity') return;
      commit();
    };
    img.addEventListener('transitionend', onEnd);
    setTimeout(commit, FADE_MS + 40);
  }

  function applyImageSwap(thumb) {
    var ctx = getCardContext(thumb);
    if (!ctx) return;
    var newSrc = thumb.getAttribute('data-image-url');
    var colourName = thumb.getAttribute('data-colour-name') || '';

    if (newSrc) {
      if (newSrc.indexOf('//') === 0) {
        newSrc = 'https:' + newSrc;
      }
      if (!ctx.img.dataset.boostDefaultSrc) {
        ctx.img.dataset.boostDefaultSrc = ctx.img.getAttribute('src') || '';
      }
      if (ctx.img.hasAttribute('srcset') && !ctx.img.dataset.boostDefaultSrcset) {
        ctx.img.dataset.boostDefaultSrcset = ctx.img.getAttribute('srcset');
      }
      fadeSwap(ctx.img, newSrc, null);
    }

    ctx.badge.textContent = colourName;
    ctx.figure.classList.add('boost-hover-active');
    ctx.card.classList.add('boost-thumb-active');

    var allPics = ctx.figure.querySelectorAll('picture');
    for (var i = 0; i < allPics.length; i++) {
      if (allPics[i].classList.contains('boost-card-main-picture')) {
        allPics[i].style.setProperty('opacity', '1', 'important');
        allPics[i].style.setProperty('z-index', '10', 'important');
      } else {
        allPics[i].style.setProperty('opacity', '0', 'important');
      }
    }
  }

  function clearImageSwap(thumb) {
    var ctx = getCardContext(thumb);
    if (!ctx) return;

    var defaultSrc = ctx.img.dataset.boostDefaultSrc;
    var defaultSrcset = ctx.img.dataset.boostDefaultSrcset;

    if (defaultSrc) {
      fadeSwap(
        ctx.img,
        defaultSrc,
        typeof defaultSrcset === 'string' ? defaultSrcset : null
      );
    }

    ctx.badge.textContent = '';
    ctx.figure.classList.remove('boost-hover-active');
    ctx.card.classList.remove('boost-thumb-active');

    var allPics = ctx.figure.querySelectorAll('picture');
    for (var i = 0; i < allPics.length; i++) {
      allPics[i].style.removeProperty('opacity');
      allPics[i].style.removeProperty('z-index');
    }
  }

  // -------- Collection-card event handlers ----------------------------------

  function handleEnter(event) {
    var target = event.target.closest(TIP_TARGETS);
    if (!target) return;

    var tipText = target.getAttribute('data-tooltip');
    if (tipText) showTip(target, tipText);

    if (target.matches(THUMB_SELECTOR)) {
      applyImageSwap(target);
    }
  }

  function handleLeave(event) {
    var target = event.target.closest(TIP_TARGETS);
    if (!target) return;

    var related = event.relatedTarget;
    if (related && related.closest && related.closest(TIP_TARGETS) === target) {
      return;
    }

    hideTip();

    if (target.matches(THUMB_SELECTOR)) {
      clearImageSwap(target);
    }
  }

  function handleScroll() {
    hideTip();
  }

  document.addEventListener('mouseover', handleEnter, true);
  document.addEventListener('mouseout', handleLeave, true);
  document.addEventListener('focusin', handleEnter, true);
  document.addEventListener('focusout', handleLeave, true);
  window.addEventListener('scroll', handleScroll, { passive: true, capture: true });

  // ── PDP Colour Selector Tooltip & Badge ──────────────────────────────────
  // - Tooltip: shows the colour name above the hovered swatch (uses the
  //   shared floating tooltip).
  // - Badge: persistent, lives at top-centre of the main gallery image
  //   (positioned via CSS), shows the currently selected colour name.
  //
  // The badge element is appended to .l4pr. After a size change the theme
  // replaces .f8pr-variant-selection but leaves .l4pr alone — so the badge
  // element persists, we just refresh its text from the now-checked radio.

  var pdpBadge = null;

  function getPdpBadge() {
    if (pdpBadge && pdpBadge.isConnected) return pdpBadge;
    var gallery = document.querySelector('.l4pr');
    if (!gallery) return null;
    // Reuse an existing badge if one is already in the DOM (e.g. after
    // hot-reload during development).
    var existing = gallery.querySelector('.boost-pdp-colour-badge');
    if (existing) {
      pdpBadge = existing;
      return pdpBadge;
    }
    pdpBadge = document.createElement('div');
    pdpBadge.className = 'boost-pdp-colour-badge';
    pdpBadge.setAttribute('aria-hidden', 'true');
    var galleryPos = window.getComputedStyle(gallery).position;
    if (galleryPos === 'static') gallery.style.position = 'relative';
    gallery.appendChild(pdpBadge);
    return pdpBadge;
  }

  function setPdpBadge(colourName) {
    var badge = getPdpBadge();
    if (!badge) return;
    if (colourName) {
      badge.textContent = colourName;
      badge.classList.add('is-visible');
    } else {
      badge.textContent = '';
      badge.classList.remove('is-visible');
    }
  }

  // BOOST CUSTOMISATION 2026-05-17 (v2.2):
  // Update the colour heading to include the selected colour name alongside
  // "Colour (N options)". Initial render comes from Liquid; this keeps it
  // in sync as the customer changes selection.
  function setColourHeadingName(colourName) {
    var nameEl = document.querySelector('[data-boost-colour-selected]');
    var sepEl = document.querySelector('.boost-pdp-colour-selected-sep');
    if (!nameEl) return;
    if (colourName) {
      nameEl.textContent = colourName;
      if (sepEl) sepEl.removeAttribute('hidden');
    } else {
      nameEl.textContent = '';
      if (sepEl) sepEl.setAttribute('hidden', '');
    }
  }

  // BOOST CUSTOMISATION 2026-06-17: the head-row shows the SELECTED colour.
  // The on-image badge no longer follows the selected colour; it follows the
  // DISPLAYED gallery slide (see "Image-driven colour badge" below). So this
  // function now only drives the head-row name.
  function refreshPdpBadgeFromChecked() {
    var checked = document.querySelector('.boost-pdp-colour-radio:checked');
    var name = checked ? checked.value : null;
    setColourHeadingName(name);
  }

  // ── Image-driven colour badge ────────────────────────────────────────────
  // GROUND TRUTH (from runtime diagnostics on a live colour PDP):
  //   * The .l4pr gallery is ONE Swiper, instance at
  //       document.querySelector('.l4pr.s4wi').children[0].swiper
  //   * It does NOT set .swiper-slide-active / data-swiper-slide-index on the
  //     slides (those read null), so slide classes are useless here. The
  //     reliable "which image is showing" signal is swiper.realIndex, which
  //     updates correctly for swatch (slideTo), pagination bullet, prev/next
  //     ARROWS, and swipe.
  //   * A colour spans MORE THAN ONE slide. The swatch data-l4pr-index is the
  //     FIRST gallery slide for that colour (e.g. 2,4,6,8,10,12 across 14
  //     slides = two images per colour, plus brand/lifestyle images at 0,1).
  //     So a colour owns every slide from its start up to the next colour's
  //     start; slides before the first colour have no colour and hide the badge.
  // Mechanism: poll swiper.realIndex on a light timer and update the badge when
  // it changes. Diagnostics proved realIndex is correct and live for every
  // navigation path (swatch, thumbnail, prev/next arrows, swipe); polling can
  // never miss a change regardless of how it was triggered or whether the theme
  // re-renders the gallery node (which broke the earlier MutationObserver).
  var colourStarts = [];        // [{idx:Number, name:String}], ascending by idx
  var lastRealIndex = -999;
  var badgePollTimer = null;
  var lastSyncedColour = null;  // guards the colour-picker sync against duplicate in-flight commits

  function buildSlideColourMap() {
    colourStarts = [];
    var radios = document.querySelectorAll('.boost-pdp-colour-radio');
    for (var i = 0; i < radios.length; i++) {
      var a = radios[i].getAttribute('data-l4pr-index');
      if (a === null || a === '') continue;
      var idx = parseInt(a, 10);
      if (isNaN(idx)) continue;
      var name = radios[i].value || radios[i].getAttribute('title') || '';
      if (name) colourStarts.push({ idx: idx, name: name });
    }
    colourStarts.sort(function (x, y) { return x.idx - y.idx; });
  }

  // The colour for a given slide index = the colour with the greatest start
  // index that is <= the slide. Below the first start -> null (hide).
  function colourForSlide(r) {
    var best = null;
    for (var i = 0; i < colourStarts.length; i++) {
      if (colourStarts[i].idx <= r) best = colourStarts[i].name;
      else break;
    }
    return best;
  }

  function setBadgeForSlide(r) {
    setPdpBadge(colourForSlide(r));
  }

  function getGallerySwiper() {
    var g = document.querySelector('.l4pr.s4wi');
    if (g && g.children[0] && g.children[0].swiper) return g.children[0].swiper;
    return null;
  }

  // ── Active-thumbnail sync (thumbs-slider rail) ────────────────────────────
  // In the theme's thumbs-slider mode the visible thumbnail rail is a CLONE of
  // the pagination bullets (custom.js clone_with_class -> '.cloned'); the theme
  // only updates that clone's active class on a direct thumb click, not when
  // the main image moves by arrow/swipe/swatch. We drive it from the same
  // realIndex signal as the colour badge so the 1px Boost Red selected outline
  // follows the displayed image on every navigation path, and we scroll the
  // rail to keep the active thumb in view. Each cloned bullet carries
  // data-l4pr-index = its slide index (custom.js sets this in the thumbs-slider
  // branch), so the bullet for the current slide is a direct lookup. Fully
  // defensive: if the rail is not present (thumbs-slider not active) or the
  // structure differs, it no-ops and leaves the stock behaviour untouched.
  function keepThumbInView(rail, target) {
    var outer = rail.querySelector('.swiper-outer');
    var sw = (outer && outer.swiper) || rail.swiper;   // create_slider puts the instance on .swiper-outer
    var slide = target.closest ? target.closest('.swiper-slide') : null;
    if (sw && slide && slide.parentNode && typeof sw.slideTo === 'function') {
      var idx = Array.prototype.indexOf.call(slide.parentNode.children, slide);
      if (idx >= 0) { sw.slideTo(idx); return; }
    }
  }

  function syncActiveThumb(r) {
    var rail = document.querySelector('.l4pr.thumbs-slider .swiper-pagination-bullets.cloned')
            || document.querySelector('.l4pr .swiper-pagination-bullets.cloned');
    if (!rail) return;   // thumbs-slider not active -> nothing to sync
    var target = rail.querySelector('.swiper-pagination-bullet[data-l4pr-index="' + r + '"]');
    if (!target) {
      var any = rail.querySelector('[data-l4pr-index="' + r + '"]');
      if (any) {
        target = any.classList && any.classList.contains('swiper-pagination-bullet')
          ? any
          : (any.closest('.swiper-pagination-bullet') || any.querySelector('.swiper-pagination-bullet'));
      }
    }
    if (!target) return;  // can't map this slide to a thumb -> leave as-is
    var actives = rail.querySelectorAll('.swiper-pagination-bullet-active');
    for (var i = 0; i < actives.length; i++) actives[i].classList.remove('swiper-pagination-bullet-active');
    target.classList.add('swiper-pagination-bullet-active');
    keepThumbInView(rail, target);
  }

  // ── Colour-picker sync (cart correctness) ─────────────────────────────────
  // The selected colour swatch (and therefore the variant that gets added to
  // cart) must match the colour currently shown in the main image. When the
  // displayed image maps to a colour different from the checked swatch, select
  // that swatch and dispatch 'change' — which the existing Boost colour-variant
  // handler in scripts.js listens for and uses to force the correct variant id
  // (native input sync + re-assert). The theme then snaps the main image to
  // that colour's primary photo (accepted behaviour). Guards:
  //   * only acts when the displayed colour differs from the checked swatch
  //     (so it never loops on the snap-back), and
  //   * lastSyncedColour blocks a duplicate commit while a section re-render is
  //     in flight. Images with no colour (lifestyle/brand) leave the selection.
  function syncColourPickerToDisplayed(colour) {
    if (!colour) return;
    var radios = document.querySelectorAll('.boost-pdp-colour-radio');
    if (!radios.length) return;
    var target = null;
    for (var i = 0; i < radios.length; i++) {
      var name = radios[i].value || radios[i].getAttribute('title') || '';
      if (name === colour) { target = radios[i]; break; }
    }
    if (!target) return;                         // no matching swatch
    if (target.checked) { lastSyncedColour = colour; return; }   // already selected
    if (lastSyncedColour === colour) return;     // commit already in flight for this colour
    lastSyncedColour = colour;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));  // reuses scripts.js colour-variant commit
  }

  // Read the live realIndex; update the badge AND the active thumbnail only when
  // the displayed slide actually changes (cheap no-op otherwise).
  function pollBadge() {
    var sw = getGallerySwiper();
    if (!sw || typeof sw.realIndex !== 'number') return;
    if (sw.realIndex === lastRealIndex) return;
    lastRealIndex = sw.realIndex;
    setBadgeForSlide(sw.realIndex);
    syncActiveThumb(sw.realIndex);
    syncColourPickerToDisplayed(colourForSlide(sw.realIndex));
  }

  function startBadgePoll() {
    if (badgePollTimer) return;
    badgePollTimer = setInterval(pollBadge, 150);
  }

  function initImageDrivenBadge() {
    buildSlideColourMap();
    lastRealIndex = -999;   // force the next poll to re-evaluate (e.g. after a variant change)
    startBadgePoll();
    pollBadge();            // try immediately if Swiper is already up
    // Immediate initial fallback while Swiper spins up: on load the displayed
    // image is the selected colour's first image.
    var checked = document.querySelector('.boost-pdp-colour-radio:checked');
    if (checked) {
      var ci = parseInt(checked.getAttribute('data-l4pr-index'), 10);
      if (!isNaN(ci)) setBadgeForSlide(ci);
    }
  }

  // Tooltip on hover. Uses the shared showTip(targetEl, text) — pass the
  // <label> element so the tooltip is positioned above the swatch.
  function handlePdpSwatchEnter(e) {
    var label = e.target && e.target.closest ? e.target.closest('.boost-pdp-colour-item label') : null;
    if (!label) return;
    var li = label.parentElement;
    if (!li) return;
    var radio = li.querySelector('.boost-pdp-colour-radio');
    if (!radio) return;
    showTip(label, radio.value);
  }

  function handlePdpSwatchLeave(e) {
    var label = e.target && e.target.closest ? e.target.closest('.boost-pdp-colour-item label') : null;
    if (!label) return;
    hideTip();
  }

  // On label click the radio toggles BEFORE this click handler fires (because
  // the label[for=...] mechanism flips the radio synchronously). We defer
  // slightly to let the theme's capture-phase change handler run, then
  // re-read the checked state.
  function handlePdpSwatchClick(e) {
    var label = e.target && e.target.closest ? e.target.closest('.boost-pdp-colour-item label') : null;
    if (!label) return;
    setTimeout(refreshPdpBadgeFromChecked, 0);
  }

  // The theme replaces .f8pr-variant-selection after a size change. The new
  // markup has whichever colour the server resolved as checked. We watch the
  // parent of .f8pr-variant-selection for child replacements, then refresh
  // the badge from the (newly-rendered) checked radio.
  function initPdpVariantObserver() {
    var variantSelection = document.querySelector('.f8pr-variant-selection');
    if (!variantSelection || !variantSelection.parentNode) return;
    var observer = new MutationObserver(function () {
      // Defer one tick so the theme's post-replacement event dispatches run
      // before we read the new state.
      setTimeout(function () {
        refreshPdpBadgeFromChecked();   // head-row (selected colour)
        initImageDrivenBadge();         // rebuild slide->colour map + observer
      }, 0);
    });
    observer.observe(variantSelection.parentNode, { childList: true });
  }

  if (document.querySelector('.boost-pdp-colour-list')) {
    document.addEventListener('mouseover', handlePdpSwatchEnter, true);
    document.addEventListener('mouseout', handlePdpSwatchLeave, true);
    document.addEventListener('click', handlePdpSwatchClick, false);
    refreshPdpBadgeFromChecked();
    initImageDrivenBadge();
    initPdpVariantObserver();
  }

  // ── Lightbox (Fancybox) colour badge + click-to-zoom ─────────────────────
  // BOOST CUSTOMISATION 2026-06-18 (Round 9).
  // The PDP main image opens in a Fancybox v4 lightbox (configured in the
  // vendor custom-async.js). Two Boost behaviours are layered on here, both
  // kept in this Boost-owned asset so they survive Xtra updates and require NO
  // vendor edits:
  //
  //   (1) Click-to-zoom. Runtime diagnostics showed the Panzoom config ships
  //       with wheel + pinch zoom enabled but click DISABLED, so on desktop a
  //       click did nothing despite the zoom-in cursor. We set the Panzoom
  //       click action to 'toggleZoom' when the lightbox opens and re-assert it
  //       on slide change (the theme calls updatePanzoom on change). Wheel and
  //       pinch are left untouched.
  //
  //   (2) Colour badge. GROUND TRUTH (runtime diagnostics on a live colour
  //       PDP): the Fancybox page index === the image anchor index === the
  //       swatch data-l4pr-index space that colourForSlide() already uses
  //       (e.g. page 6 == anchor[6] == Midnight Blue == swatch start 6). So the
  //       displayed lightbox colour is colourForSlide(anchorIndexOfImage). We
  //       map the selected slide's image basename back to its [data-fancybox]
  //       anchor index (robust to Fancybox's src de-duplication), then reuse
  //       colourForSlide(). Leading hero/lifestyle shots sit before the first
  //       colour start and show no badge, exactly as on the PDP.
  //
  //   The container's data-class attribute is NOT a reliable change signal (it
  //   read null in diagnostics), so we watch the selected slide directly.

  var fbBadge = null;
  var fbSrcToIndex = null;     // { imageBasename: anchorIndex }
  var fbScopedObserver = null;
  var fbLastSrc = null;

  function fbBasename(u) {
    if (!u) return '';
    return u.split('/').pop().split('?')[0];
  }

  function buildFbSrcMap() {
    fbSrcToIndex = {};
    var a0 = document.querySelector('[data-fancybox]');
    if (!a0) return;
    var name = a0.getAttribute('data-fancybox');
    if (!name) return;
    var anchors = document.querySelectorAll('[data-fancybox="' + name + '"]');
    for (var i = 0; i < anchors.length; i++) {
      var key = fbBasename(anchors[i].getAttribute('href'));
      if (key && fbSrcToIndex[key] === undefined) fbSrcToIndex[key] = i;
    }
  }

  function getFbBadge(container) {
    if (fbBadge && fbBadge.isConnected) return fbBadge;
    var existing = container.querySelector('.boost-fb-colour-badge');
    if (existing) { fbBadge = existing; return fbBadge; }
    fbBadge = document.createElement('div');
    fbBadge.className = 'boost-fb-colour-badge';
    fbBadge.setAttribute('aria-hidden', 'true');
    container.appendChild(fbBadge);
    return fbBadge;
  }

  function setFbBadge(container, colourName) {
    var badge = getFbBadge(container);
    if (!badge) return;
    if (colourName) {
      badge.textContent = colourName;
      badge.classList.add('is-visible');
    } else {
      badge.textContent = '';
      badge.classList.remove('is-visible');
    }
  }

  // Enable desktop click-to-zoom. Guarded so it is a cheap no-op once set.
  function enableFbClickZoom() {
    try {
      var inst = (window.Fancybox && Fancybox.getInstance) ? Fancybox.getInstance() : null;
      if (!inst || !inst.Carousel || !inst.Carousel.Panzoom) return;
      var pz = inst.Carousel.Panzoom;
      if (pz.options && pz.options.click !== 'toggleZoom') {
        pz.options.click = 'toggleZoom';
        if (typeof inst.Carousel.updatePanzoom === 'function') inst.Carousel.updatePanzoom();
      }
    } catch (e) { /* never break the lightbox */ }
  }

  function updateFb(container) {
    enableFbClickZoom();
    var sel = container.querySelector('.fancybox__slide.is-selected');
    if (!sel) return;
    var img = sel.querySelector('img.fancybox__image') || sel.querySelector('img');
    if (!img) { setFbBadge(container, null); return; }
    var src = fbBasename(img.currentSrc || img.getAttribute('src') || img.src || '');
    if (src && src === fbLastSrc) return;   // same image -> skip (also ignores zoom-state class churn)
    fbLastSrc = src;
    if (!fbSrcToIndex) buildFbSrcMap();
    var idx = (fbSrcToIndex && (src in fbSrcToIndex)) ? fbSrcToIndex[src] : null;
    var colour = (idx === null || idx === undefined) ? null : colourForSlide(idx);
    setFbBadge(container, colour);
  }

  function onFbOpen(container) {
    buildSlideColourMap();   // refresh colour starts from the live swatches
    buildFbSrcMap();         // map image basenames -> anchor index
    fbLastSrc = null;
    fbBadge = null;
    if (fbScopedObserver) { fbScopedObserver.disconnect(); fbScopedObserver = null; }
    // Watch the selected slide for class moves (slide change) and lazy image
    // loads. rAF-coalesced because many class mutations fire per nav/zoom tick.
    fbScopedObserver = new MutationObserver(function () {
      if (container.__boostFbRaf) return;
      container.__boostFbRaf = requestAnimationFrame(function () {
        container.__boostFbRaf = null;
        updateFb(container);
      });
    });
    fbScopedObserver.observe(container, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
    // Deferred passes while the first image + Panzoom spin up.
    updateFb(container);
    setTimeout(function () { updateFb(container); }, 60);
    setTimeout(function () { updateFb(container); }, 250);
  }

  function onFbClose() {
    if (fbScopedObserver) { fbScopedObserver.disconnect(); fbScopedObserver = null; }
    fbBadge = null;
    fbLastSrc = null;
  }

  // Detect the lightbox opening/closing at the document level: the
  // .fancybox__container is appended to <body> on open and removed on close.
  var fbBodyObserver = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var a = 0; a < added.length; a++) {
        var n = added[a];
        if (n.nodeType !== 1) continue;
        var cont = (n.classList && n.classList.contains('fancybox__container')) ? n
                 : (n.querySelector ? n.querySelector('.fancybox__container') : null);
        if (cont) onFbOpen(cont);
      }
      var removed = mutations[m].removedNodes;
      for (var r = 0; r < removed.length; r++) {
        var rn = removed[r];
        if (rn.nodeType !== 1) continue;
        if ((rn.classList && rn.classList.contains('fancybox__container')) ||
            (rn.querySelector && rn.querySelector('.fancybox__container'))) {
          onFbClose();
        }
      }
    }
  });
  if (document.body) fbBodyObserver.observe(document.body, { childList: true });

  // ── Remove VIP (vendor = "Boost Uniforms") cards from recently-viewed
  // and recommendations sections. Recommendations are filtered in Liquid
  // (product-recommendations.liquid). Recently-viewed is JS-rendered after
  // page load, so we watch for DOM insertions via MutationObserver and strip
  // any newly-added cards with data-vendor="Boost Uniforms".
  var EXCLUDED_VENDOR = 'Boost Uniforms';

  function removeVipCards(root) {
    var cards = root.querySelectorAll('[data-vendor="' + EXCLUDED_VENDOR + '"]');
    var removed = 0;
    for (var i = 0; i < cards.length; i++) {
      // BOOST CUSTOMISATION 2026-07-09 (T-00384): never strip cards inside the
      // search results grid. Decoration and setup-fee products (vendor
      // 'Boost Uniforms') are intentionally shown there now; this document-wide
      // strip is meant only for the JS-rendered recently-viewed section. Without
      // this guard the strip deleted those cards right after render and left
      // blank grid slots behind.
      if (cards[i].closest && cards[i].closest('[data-boost-search-grid]')) {
        continue;
      }
      if (cards[i].parentNode) {
        cards[i].parentNode.removeChild(cards[i]);
        removed++;
      }
    }
    if (removed > 0) {
      window.dispatchEvent(new Event('resize'));
    }
  }

  removeVipCards(document);

  var rvObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.dataset && (node.dataset.vendor === EXCLUDED_VENDOR || node.dataset.vendor === 'VIP - Boost Uniforms')) {
          if (!(node.closest && node.closest('[data-boost-search-grid]'))) {
            node.parentNode && node.parentNode.removeChild(node);
          }
          continue;
        }
        removeVipCards(node);
        // BOOST CUSTOMISATION 2026-07-10 (VIP): this observer is scoped to the
        // recently-viewed carousel, so also strip any VIP customer cards
        // (vendor 'VIP - Boost Uniforms') nested in the injected node. Fires on
        // injection, so it reliably catches cards that fetch in after the
        // populate event (which the timed strip below could miss).
        if (node.querySelectorAll) {
          var vipNested = node.querySelectorAll('[data-vendor="VIP - Boost Uniforms"]');
          for (var v = 0; v < vipNested.length; v++) {
            if (vipNested[v].parentNode) vipNested[v].parentNode.removeChild(vipNested[v]);
          }
        }
      }
    }
  });

  var rvSection = document.querySelector('.recently-viewed-products');
  if (rvSection) {
    rvObserver.observe(rvSection, { childList: true, subtree: true });
  }

  // BOOST CUSTOMISATION 2026-07-09 (T-00512): the recently-viewed carousel is
  // populated from localStorage after this script runs, and the batched inject
  // can land before the observer is watching, so Boost 'Boost Uniforms' cards
  // (now viewable since decoration/setup-fee products are searchable) were
  // slipping through. Re-run the strip when the populate event fires, once
  // immediately and once after a short delay to cover the inject timing.
  window.addEventListener('recentlyViewedProducts', function () {
    function strip() {
      var rv = document.querySelector('.recently-viewed-products');
      if (!rv) return;
      removeVipCards(rv);
      // BOOST CUSTOMISATION 2026-07-10 (VIP): also remove VIP customer products
      // (vendor 'VIP - Boost Uniforms') from the recently-viewed carousel only.
      // This catches any already-recorded VIP item and stops it showing to a
      // later non-entitled session on the same browser. Scoped to the carousel,
      // so VIP collections (where these cards belong) are untouched.
      var vip = rv.querySelectorAll('[data-vendor="VIP - Boost Uniforms"]');
      var removed = 0;
      for (var i = 0; i < vip.length; i++) {
        if (vip[i].parentNode) { vip[i].parentNode.removeChild(vip[i]); removed++; }
      }
      if (removed > 0) window.dispatchEvent(new Event('resize'));
    }
    strip();
    setTimeout(strip, 300);
    setTimeout(strip, 900);
  });

  // ── Collection colour pager (Round 11) ───────────────────────────────────
  // Cards with more than 5 colours render a paged colour rail (see
  // product-item.liquid): 4 thumbnails visible, half-width prev/next chevrons.
  // We scroll the viewport one colour per chevron click and show/hide the
  // chevrons at the ends (they disappear rather than grey). For products with
  // more than 12 colours only the first 12 render; at the end the right chevron
  // becomes a "Show All Colours" link to the product page (the <a> default
  // navigation is allowed through; data-boost-colour-overflow marks the card).
  // Hover-swap and tooltips on the thumbnails are handled above and unchanged.
  var COLOUR_STEP_FALLBACK = 34;   // 30px thumbnail + 4px gap

  function pagerStep(pager) {
    var item = pager.querySelector('.boost-colour-thumbnail-item');
    if (item) {
      var w = item.getBoundingClientRect().width;
      if (w > 0) return Math.round(w) + 4;   // + gap
    }
    return COLOUR_STEP_FALLBACK;
  }

  function updatePager(pager) {
    var vp = pager.querySelector('.boost-colour-viewport');
    var prev = pager.querySelector('.boost-colour-prev');
    var next = pager.querySelector('.boost-colour-next');
    if (!vp || !next) return;
    var maxScroll = vp.scrollWidth - vp.clientWidth;
    var x = vp.scrollLeft;
    var atStart = x <= 1;
    var atEnd = x >= maxScroll - 1;
    var overflow = pager.getAttribute('data-boost-colour-overflow');   // product url, or null

    // Prev: hidden at the start, shown otherwise.
    if (prev) {
      if (atStart) prev.setAttribute('hidden', '');
      else prev.removeAttribute('hidden');
    }

    // Next: "Next" pager while there is more to the right. At the end either
    // disappears (<=12 colours, all shown) or becomes "Show All Colours"
    // (>12 colours, links to the product page).
    if (atEnd && overflow) {
      next.removeAttribute('hidden');
      next.setAttribute('data-tooltip', 'Show All Colours');
      next.setAttribute('data-boost-colour-showall', '1');
    } else if (atEnd) {
      next.setAttribute('hidden', '');
      next.removeAttribute('data-boost-colour-showall');
      next.setAttribute('data-tooltip', 'Next');
    } else {
      next.removeAttribute('hidden');
      next.removeAttribute('data-boost-colour-showall');
      next.setAttribute('data-tooltip', 'Next');
    }
  }

  function initColourPager(pager) {
    if (pager.__boostPagerInit) return;
    pager.__boostPagerInit = true;
    var vp = pager.querySelector('.boost-colour-viewport');
    var prev = pager.querySelector('.boost-colour-prev');
    var next = pager.querySelector('.boost-colour-next');
    if (!vp) return;

    if (prev) {
      prev.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();   // don't let a parent carousel act on it
        vp.scrollLeft -= pagerStep(pager);
      });
    }
    if (next) {
      next.addEventListener('click', function (e) {
        // In the show-all state, let the <a> navigate to the product page.
        if (next.getAttribute('data-boost-colour-showall')) {
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        vp.scrollLeft += pagerStep(pager);
      });
    }

    // Keep the chevrons in sync as the strip scrolls (covers chevron clicks and
    // touch swipe). rAF-coalesced.
    vp.addEventListener('scroll', function () {
      if (pager.__boostPagerRaf) return;
      pager.__boostPagerRaf = requestAnimationFrame(function () {
        pager.__boostPagerRaf = null;
        updatePager(pager);
      });
    }, { passive: true });

    // Stop a touch swipe on the strip from dragging a parent carousel.
    vp.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });
    vp.addEventListener('pointerdown', function (e) { e.stopPropagation(); });

    updatePager(pager);
  }

  function initColourPagers(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var pagers = scope.querySelectorAll('[data-boost-colour-pager]');
    for (var i = 0; i < pagers.length; i++) initColourPager(pagers[i]);
    if (root && root.nodeType === 1 && root.matches && root.matches('[data-boost-colour-pager]')) {
      initColourPager(root);
    }
  }

  initColourPagers(document);

  // Re-init for cards added later: filter/search AJAX re-render, load-more,
  // carousels, recently-viewed. Idempotent via the __boostPagerInit flag.
  var pagerObserver = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var a = 0; a < added.length; a++) {
        var n = added[a];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches('[data-boost-colour-pager]')) initColourPager(n);
        if (n.querySelectorAll) {
          var inner = n.querySelectorAll('[data-boost-colour-pager]');
          for (var k = 0; k < inner.length; k++) initColourPager(inner[k]);
        }
      }
    }
  });
  if (document.body) pagerObserver.observe(document.body, { childList: true, subtree: true });
})();

/* ============================================
BOOST CUSTOMISATION END
============================================ */
